import _ from "lodash";
import { SaleBidder, SaleContactBidder } from "../bidders/BidderData";
import { SaleLot } from "../lots/LotData";
import { ContactCredit } from "../operation/contacts/credits/ContactCreditData";
import { SaleInvoiceModifier } from "../sales/InvoiceModifiers/SaleInvoiceModifierData";
import { Payment } from "./payments/InvoicePaymentData";
import dayjs from "dayjs";
import { AgTechContact } from "@agtech/data/contacts/ContactData";
import { ContactNote } from "@agtech/data/contacts/notes/ContactNoteData";
import { AgTechIndex } from "@agtech/core/utilities/AgTechUtilities";

export type BidderLotSaleReadyForInvoicing = {
    id: number,
    bidder: SaleBidder,
    numberOfLots: number,
    totalPurchasePrice: number
}

export type InvoiceInfo = {
    id: number,
    invoiceNumber: string,
    invoiceTotal: number,
    numberOfLots: number,
    numberOfInvoiceModifiersApplied?: number,
    numberOfCreditsApplied?: number,
    purchaserName: string,
    invoiceDate: Date,
    contactId: number,
    contactBidder: SaleContactBidder
}

export enum InvoiceStatus {
    'Paid',
    'Unpaid',
    'Partially Paid'
}

export type InvoiceDetails = {
    invoice: {
        id: number,
        status: string,
        timestamp: string,
        info: {
            date: string,
            number: string,
            total: number,
            status: InvoiceStatus
        },
        details: {
            creditsApplied: ContactCredit[],
            InvoiceModifiersGiven: SaleInvoiceModifier[],
            lotsPurchased: SaleLot[],
            paymentsMade: Payment[]
        }
    },
    contact: {
        id: number,
        info: AgTechContact,
        details: {
            notes: ContactNote[],
            credits: ContactCredit[]
        }
    }
}

export type InvoiceTotal = {
    lotPurchaseTotal: number,
    appliedCreditsTotal: number,
    appliedInvoiceModifiersTotal: number,
    invoiceTotal: number
}

const calculateLotPurchaseTotal = (selectedLots: SaleLot[]): number => {
    return _.sum(selectedLots.map(l => (l.purchasePrice ?? 0) * (l.groupSize ?? 1)));
}

const calculateCreditTotal = (selectedCredits: ContactCredit[]): number => {
    return _.sum(selectedCredits.map(c => c.creditAmount ?? 0));
}

export const calculateInvoiceModifierTotal = (sumOfLotsSold: number, appliedInvoiceModifiers: SaleInvoiceModifier[]): number => {
    let invoiceInvoiceModifierTotal = 0;

    appliedInvoiceModifiers.forEach(d => {
        let InvoiceModifierAmount = d.isPercentage
            ? ((d.amount / 100.0) * sumOfLotsSold)
            : d.amount;

        invoiceInvoiceModifierTotal += InvoiceModifierAmount;
    });

    return invoiceInvoiceModifierTotal;
}

export const calculateInvoiceTotal = (props: {
    selectedLots: SaleLot[],
    appliedInvoiceModifiers: SaleInvoiceModifier[],
    appliedCredits: ContactCredit[]
}): InvoiceTotal => {
    const lotPurchaseTotal = calculateLotPurchaseTotal(props.selectedLots);
    const creditTotal = calculateCreditTotal(props.appliedCredits);
    const InvoiceModifierTotal = calculateInvoiceModifierTotal(lotPurchaseTotal, props.appliedInvoiceModifiers);

    return {
        lotPurchaseTotal,
        appliedCreditsTotal: creditTotal,
        appliedInvoiceModifiersTotal: InvoiceModifierTotal,
        invoiceTotal: lotPurchaseTotal - InvoiceModifierTotal - creditTotal
    }
}

export const getInvoiceFileName = (props: {
    invoiceContact: AgTechContact,
    invoiceNumber: string,
    invoiceDate: string
}) => {
    return `Invoice_${props.invoiceNumber}_${props.invoiceContact.firstName}_${props.invoiceContact.lastName}_${dayjs(props.invoiceDate).format('MM_DD_YYYY')}.pdf`
}